import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFacility } from '../context/FacilityContext';
import { AlertTriangle, ArrowLeft, Check } from 'lucide-react';
import unitsService from '../services/unitsService';
import apiClient from '../utils/apiClient';
import unitTypesService from '../services/unitTypesService';
import HelpLink from '../components/HelpLink';

/**
 * Mass Edit Rental Prices — operator's annual rate increase tool.
 *
 * Four steps in one page:
 *   1. Filter scope (unit types, size range, current-rate floor)
 *   2. Action (flat $ / percent / set-to-target)
 *   3. Effective date
 *   4. Preview (server returns the affected rows + summary delta)
 *   5. Confirm (POST again with preview_only=false)
 *
 * Backend: POST /api/facilities/{fid}/units/bulk-rate-change
 *
 * DOES NOT email tenants. Operator handles the legally-required 30-day
 * rate-change notice separately. The yellow callout below reinforces this.
 */

const todayIso = () => new Date().toISOString().slice(0, 10);

const Card = ({ title, subtitle, children, num }) => (
  <div className="rounded-xl border border-slate-200/80 bg-white p-5 mb-4">
    <div className="flex items-baseline justify-between mb-3">
      <div>
        {num && (
          <span className="inline-block text-[10px] uppercase tracking-wide text-slate-400 mr-2">
            Step {num}
          </span>
        )}
        <span className="text-[14px] font-semibold text-slate-900">{title}</span>
      </div>
      {subtitle && <span className="text-[12px] text-slate-500">{subtitle}</span>}
    </div>
    {children}
  </div>
);

const labelCls = 'block text-[12px] font-medium text-slate-700 mb-1';
const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500';

const MassRateChange = () => {
  const { facilityId, currentFacility } = useFacility();
  const navigate = useNavigate();
  const [unitTypes, setUnitTypes] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [minSize, setMinSize] = useState('');
  const [maxSize, setMaxSize] = useState('');
  const [rateBelow, setRateBelow] = useState('');
  const [changeType, setChangeType] = useState('flat'); // flat | percent | set
  const [value, setValue] = useState('');
  const [effective, setEffective] = useState('immediate'); // immediate | next_cycle | date
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    if (!facilityId) return;
    unitTypesService
      .getAll(facilityId)
      .then((res) => setUnitTypes(res.data || []))
      .catch(() => setUnitTypes([]));
  }, [facilityId]);

  const buildBody = (previewOnly) => {
    const filter = {};
    if (selectedTypeIds.length) filter.unit_type_ids = selectedTypeIds;
    if (minSize !== '') filter.min_size_sqft = Number(minSize);
    if (maxSize !== '') filter.max_size_sqft = Number(maxSize);
    if (rateBelow !== '') filter.rate_below = Number(rateBelow);

    const effDate =
      effective === 'immediate'
        ? todayIso()
        : effective === 'next_cycle'
          ? todayIso() // billing job decides the actual gate
          : effectiveDate;

    return {
      filter,
      change_type: changeType,
      value: Number(value),
      effective_date: effDate,
      preview_only: previewOnly,
    };
  };

  const onPreview = async () => {
    setError(null);
    setApplied(null);
    if (value === '' || Number.isNaN(Number(value))) {
      setError('Enter a numeric value for the change.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await unitsService.bulkRateChange(facilityId, buildBody(true));
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not preview the change.');
    } finally {
      setSubmitting(false);
    }
  };

  const onApply = async () => {
    if (!preview) return;
    if (
      !window.confirm(
        `Apply rate change to ${preview.summary.units_to_change} unit(s)? ` +
          `Monthly revenue change: $${preview.summary.monthly_revenue_delta.toFixed(2)}. ` +
          `This cannot be undone in one click.`,
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await unitsService.bulkRateChange(facilityId, buildBody(false));
      setApplied(res.data);
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not apply the change.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleType = (id) => {
    setSelectedTypeIds((curr) =>
      curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id],
    );
    setPreview(null);
  };

  if (!facilityId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
        <p className="text-[13px] text-slate-500 mt-1">
          Pick a facility before changing rates.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <Link
        to="/units"
        className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-700 mb-3"
      >
        <ArrowLeft className="h-3 w-3" /> Back to Units
      </Link>

      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900">Mass Rate Change <HelpLink slug="rate-changes" /></h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Update unit and rental rates in bulk for {currentFacility?.name}.
        </p>
      </div>

      <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 p-3.5 mb-4 flex items-start gap-2.5">
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-[12px] text-amber-800">
          This changes rates only. You must send the legally-required rate-change notice
          to affected tenants separately. No automated emails are sent from this tool.
        </p>
      </div>

      <Card num="1" title="Filter — which units">
        <div className="mb-4">
          <label className={labelCls}>Unit types</label>
          {unitTypes.length === 0 ? (
            <p className="text-[12px] text-slate-400 italic">No unit types yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {unitTypes.map((t) => {
                const active = selectedTypeIds.includes(t._id);
                return (
                  <button
                    type="button"
                    key={t._id}
                    onClick={() => toggleType(t._id)}
                    className={`text-[12px] font-medium rounded-full px-3 py-1 border transition-colors ${
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {t.name} {t.monthly_rate != null && (
                      <span className="text-slate-400 ml-1">${Number(t.monthly_rate).toFixed(0)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-1.5">
            Empty = all unit types in the facility.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Min size (sqft)</label>
            <input
              type="number"
              className={inputCls}
              value={minSize}
              onChange={(e) => { setMinSize(e.target.value); setPreview(null); }}
            />
          </div>
          <div>
            <label className={labelCls}>Max size (sqft)</label>
            <input
              type="number"
              className={inputCls}
              value={maxSize}
              onChange={(e) => { setMaxSize(e.target.value); setPreview(null); }}
            />
          </div>
          <div>
            <label className={labelCls}>Only if current rate &lt; ($)</label>
            <input
              type="number"
              step="0.01"
              className={inputCls}
              value={rateBelow}
              onChange={(e) => { setRateBelow(e.target.value); setPreview(null); }}
            />
          </div>
        </div>
      </Card>

      <Card num="2" title="Action — how to change">
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { value: 'flat', label: 'Flat $ change (e.g. +10)' },
            { value: 'percent', label: 'Percent (e.g. 5 = +5%)' },
            { value: 'set', label: 'Set to ($)' },
          ].map((o) => (
            <button
              type="button"
              key={o.value}
              onClick={() => { setChangeType(o.value); setPreview(null); }}
              className={`text-[12px] font-medium rounded-full px-3 py-1 border transition-colors ${
                changeType === o.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div>
          <label className={labelCls}>
            Value {changeType === 'percent' && '(percent — e.g. 5 for 5%)'}
            {changeType === 'flat' && '(dollars — positive to raise, negative to lower)'}
            {changeType === 'set' && '(target rate in dollars)'}
          </label>
          <input
            type="number"
            step="0.01"
            className={inputCls}
            value={value}
            onChange={(e) => { setValue(e.target.value); setPreview(null); }}
          />
        </div>
      </Card>

      <Card num="3" title="Effective date">
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { value: 'immediate', label: 'Immediate (this month)' },
            { value: 'next_cycle', label: 'Next billing cycle' },
            { value: 'date', label: 'Pick a date' },
          ].map((o) => (
            <button
              type="button"
              key={o.value}
              onClick={() => { setEffective(o.value); setPreview(null); }}
              className={`text-[12px] font-medium rounded-full px-3 py-1 border transition-colors ${
                effective === o.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {effective === 'date' && (
          <input
            type="date"
            className={inputCls}
            value={effectiveDate}
            onChange={(e) => { setEffectiveDate(e.target.value); setPreview(null); }}
          />
        )}
      </Card>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 mb-4 text-[13px] text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          disabled={submitting}
          onClick={onPreview}
          className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Calculating…' : 'Preview affected units'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/units')}
          className="text-[13px] text-slate-500 hover:text-slate-700"
        >
          Cancel
        </button>
      </div>

      {preview && (
        <Card
          num="4"
          title="Preview"
          subtitle={`${preview.summary.units_matched} unit(s) matched`}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px] text-slate-700 mb-4">
            <div>
              <div className="text-slate-400">Will change</div>
              <div className="text-base font-semibold text-slate-900">
                {preview.summary.units_to_change}
              </div>
            </div>
            <div>
              <div className="text-slate-400">Skipped</div>
              <div className="text-base font-semibold text-slate-900">
                {preview.summary.units_skipped}
              </div>
            </div>
            <div>
              <div className="text-slate-400">Active rentals</div>
              <div className="text-base font-semibold text-slate-900">
                {preview.summary.rentals_to_change}
              </div>
            </div>
            <div>
              <div className="text-slate-400">Monthly delta</div>
              <div
                className={`text-base font-semibold ${
                  preview.summary.monthly_revenue_delta >= 0
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
              >
                ${preview.summary.monthly_revenue_delta.toFixed(2)}
              </div>
            </div>
          </div>

          {preview.rows.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-slate-200/80">
              <table className="min-w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Unit', 'Type', 'Current', 'New', 'Delta', 'Tenant', 'Status'].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-left"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.unit_id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2 text-[13px] font-medium text-slate-900">
                        {r.unit_number}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-slate-600">
                        {r.unit_type || '—'}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-slate-600 tabular-nums">
                        ${r.current_rate.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-slate-900 tabular-nums">
                        ${r.new_rate.toFixed(2)}
                      </td>
                      <td
                        className={`px-3 py-2 text-[13px] tabular-nums ${
                          r.delta >= 0 ? 'text-emerald-700' : 'text-rose-700'
                        }`}
                      >
                        ${r.delta.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-slate-600">
                        {r.tenant_name || (r.has_active_rental ? '—' : 'Vacant')}
                      </td>
                      <td className="px-3 py-2 text-[12px]">
                        {r.skipped ? (
                          <span className="text-amber-700">Skip: {r.skip_reason}</span>
                        ) : (
                          <span className="text-emerald-700">Apply</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[13px] text-slate-500 italic">
              No units matched the filter.
            </p>
          )}

          {applied?.applied ? (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-[13px] text-emerald-800">
              <Check className="h-4 w-4" />
              Applied. {applied.summary.units_to_change} unit(s) updated.
              Audit log id: {applied.audit_log_id}.
            </div>
          ) : preview.summary.units_to_change > 0 ? (
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                disabled={submitting}
                onClick={onApply}
                className="rounded-lg bg-emerald-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-emerald-700 disabled:opacity-50"
              >
                {submitting ? 'Applying…' : `Apply to ${preview.summary.units_to_change} unit(s)`}
              </button>
            </div>
          ) : null}
        </Card>
      )}

      <ScheduledTenantRateChanges facilityId={facilityId} unitTypes={unitTypes} />
    </div>
  );
};


// ── Scheduled TENANT rate changes (with notice) ────────────────────────────
// Distinct from the street-rate tool above: this targets EXISTING tenants'
// monthly rate, notifies them immediately (email+SMS, editable template),
// and applies automatically to billing periods on/after the effective date.
const ScheduledTenantRateChanges = ({ facilityId, unitTypes }) => {
  const [scope, setScope] = useState('all');           // all | unit_type
  const [unitType, setUnitType] = useState('');
  const [amountMode, setAmountMode] = useState('percent'); // percent | set
  const [amount, setAmount] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const loadPending = React.useCallback(() => {
    if (!facilityId) return;
    apiClient.get(`/api/facilities/${facilityId}/rate-changes/pending`)
      .then((r) => setPending(r.data))
      .catch(() => setPending([]));
  }, [facilityId]);
  useEffect(() => { loadPending(); }, [loadPending]);

  const buildBody = (previewOnly) => {
    const body = { effective_date: effectiveDate, preview_only: previewOnly };
    if (scope === 'unit_type') body.unit_type = unitType;
    else body.all_active = true;
    if (amountMode === 'percent') body.percent_increase = parseFloat(amount);
    else body.new_rate = parseFloat(amount);
    return body;
  };

  const run = async (previewOnly) => {
    setNotice(null);
    if (!amount || !effectiveDate) {
      setNotice({ ok: false, text: 'Set the amount and a future effective date.' });
      return;
    }
    if (scope === 'unit_type' && !unitType) {
      setNotice({ ok: false, text: 'Pick a unit type.' });
      return;
    }
    setBusy(true);
    try {
      const r = await apiClient.post(
        `/api/facilities/${facilityId}/rate-changes`, buildBody(previewOnly));
      if (previewOnly) {
        setPreview(r.data);
      } else {
        setPreview(null);
        setNotice({ ok: true,
          text: `Scheduled ${r.data.scheduled} change(s); ${r.data.notices_sent} tenant notice(s) sent.` });
        loadPending();
      }
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.detail || 'Request failed.' });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (rentalId) => {
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/rate-changes/${rentalId}`);
      loadPending();
    } catch { /* noop */ }
  };

  const fieldCls = 'border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500';

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-5 mt-6">
      <h2 className="text-[15px] font-semibold text-slate-900">Scheduled tenant rate changes</h2>
      <p className="text-[12px] text-slate-500 mb-4">
        Raise (or change) existing tenants' monthly rate on a future date. Each tenant is
        notified immediately by email/text; billing switches automatically for periods on
        or after the effective date.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[12px] font-medium text-slate-600 mb-1">Who</label>
          <select className={fieldCls} value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="all">All active rentals</option>
            <option value="unit_type">One unit type</option>
          </select>
        </div>
        {scope === 'unit_type' && (
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">Unit type</label>
            <select className={fieldCls} value={unitType} onChange={(e) => setUnitType(e.target.value)}>
              <option value="">Select…</option>
              {(unitTypes || []).map((t) => (
                <option key={t._id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[12px] font-medium text-slate-600 mb-1">Change</label>
          <select className={fieldCls} value={amountMode} onChange={(e) => setAmountMode(e.target.value)}>
            <option value="percent">Increase by %</option>
            <option value="set">Set new rate $</option>
          </select>
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-600 mb-1">
            {amountMode === 'percent' ? 'Percent' : 'New rate'}
          </label>
          <input type="number" step="0.01" className={`${fieldCls} w-28`}
                 value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-slate-600 mb-1">Effective date</label>
          <input type="date" className={fieldCls} value={effectiveDate}
                 min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                 onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>
        <button onClick={() => run(true)} disabled={busy}
                className="rounded-lg bg-blue-600 text-[13px] font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50">
          Preview
        </button>
      </div>

      {notice && (
        <p className={`text-[12px] mt-3 ${notice.ok ? 'text-emerald-600' : 'text-red-500'}`}>{notice.text}</p>
      )}

      {preview && (
        <div className="mt-4">
          <div className="overflow-x-auto">
            <table className="w-full max-w-xl">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-left">
                  <th className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase">Unit</th>
                  <th className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase">Current</th>
                  <th className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase">New</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {preview.rows.map((row) => (
                  <tr key={row.rental_id}>
                    <td className="px-3 py-1.5 text-[13px] text-slate-700">{row.unit_number || '—'}</td>
                    <td className="px-3 py-1.5 text-[13px] text-slate-500">${row.old_rate}</td>
                    <td className="px-3 py-1.5 text-[13px] font-medium text-slate-900">
                      ${row.new_rate}{row.skipped ? ` (${row.skipped})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={() => run(false)} disabled={busy}
                  className="mt-3 rounded-lg bg-emerald-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-emerald-700 disabled:opacity-50">
            {busy ? 'Scheduling…' : `Schedule ${preview.rows.filter((r) => !r.skipped).length} change(s) + send notices`}
          </button>
        </div>
      )}

      {(pending || []).length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <h3 className="text-[13px] font-semibold text-slate-900 mb-2">Pending changes</h3>
          <div className="overflow-x-auto">
            <table className="w-full max-w-2xl">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-left">
                  <th className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase">Unit · Tenant</th>
                  <th className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase">Change</th>
                  <th className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase">Effective</th>
                  <th className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase text-right">Cancel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {pending.map((p) => (
                  <tr key={p.rental_id}>
                    <td className="px-3 py-1.5 text-[13px] text-slate-700">
                      {p.unit_number || '—'} · {p.customer_name || '—'}
                    </td>
                    <td className="px-3 py-1.5 text-[13px] text-slate-500">
                      ${p.old_rate} → <span className="font-medium text-slate-900">${p.new_rate}</span>
                    </td>
                    <td className="px-3 py-1.5 text-[13px] text-slate-500">{p.effective_date}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => cancel(p.rental_id)}
                              className="text-[12px] text-slate-400 hover:text-red-500 font-medium">
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MassRateChange;
